/**
 * Firestore / PG reads for submission-status artefacts.
 *
 * Fax artefacts are read PG-first from `submission.submission_output` with a
 * Firestore `submission_info/output` fallback. Clinical-document path still
 * falls back to the Firestore order doc when frontend-query has no path.
 *
 * @module submissionFirestoreFallback
 */
import { FirestoreService } from "../../../api/firebase/firestoreService";
import { logError } from "../../../utils/customLogger";
import {
  FaxSubmissionOutput,
  readFaxSubmissionOutput,
} from "../../../utils/faxSubmissionOutputPersistence";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";

/**
 * When `true`, clinical-document Firestore fallback remains available.
 * Fax output always uses {@link readFaxSubmissionOutput} (PG-first + FS).
 */
export const SUBMISSION_STATUS_FIRESTORE_FALLBACK = true;

/** Fax output artefacts (`submission_output` / Firestore output doc). */
export type FaxOutputFallback = FaxSubmissionOutput;

/**
 * Reads fax output PG-first, with Firestore fallback when PG is empty/missing.
 *
 * @input orderId - The PA order id whose fax output is read.
 * @returns Fax details id, filled-pdf link, form id, fax status, when present.
 */
export const fetchFaxOutputFallback = async (
  orderId: string,
): Promise<FaxOutputFallback> => {
  if (!orderId) {
    return {};
  }
  try {
    const orgId = getOrgIdForFetchExternalWorklist();
    return await readFaxSubmissionOutput(orderId, orgId);
  } catch (error) {
    logError(error as Error, "Fax output record not found");
    return {};
  }
};

/**
 * Reads the order's `clinical_document.file_path` from the Firestore order doc.
 *
 * Used only when Postgres frontend-query did not return a clinical document
 * path. Returns `undefined` when the fallback flag is off, the doc is missing,
 * or the read fails.
 *
 * @input orderId - The PA order id whose clinical-document path is read.
 * @returns The clinical-document storage path, when present.
 */
export const fetchClinicalDocumentPathFallback = async (
  orderId: string,
): Promise<string | undefined> => {
  if (!SUBMISSION_STATUS_FIRESTORE_FALLBACK || !orderId) {
    return undefined;
  }
  try {
    const order = await FirestoreService.getDocument<{
      clinical_document?: { file_path?: string };
    }>(`medical_pa_orders/${orderId}`);
    return order?.clinical_document?.file_path;
  } catch (error) {
    logError(error as Error, "Clinical document path not found");
    return undefined;
  }
};
